/**
 * AI ASSISTANT CONFIGURATION MODELS
 * 
 * Configuración del asistente IA supervisado por el terapeuta.
 * El terapeuta define:
 * - Qué prácticas puede sugerir la IA
 * - En qué situaciones puede actuar
 * - Cuándo debe escalar a humanos
 * - Mensajes personalizados
 */

import { AISituationType } from '../content/user-practice-library.model';

// ============================================
// ENUMS
// ============================================

export enum EscalationTrigger {
  CRISIS = 'crisis',
  SUICIDAL_IDEATION = 'suicidal_ideation',
  SELF_HARM = 'self_harm',
  SEVERE_ANXIETY = 'severe_anxiety',
  PANIC_ATTACK = 'panic_attack',
  DISSOCIATION = 'dissociation',
  PSYCHOTIC_SYMPTOMS = 'psychotic_symptoms',
  SUBSTANCE_ABUSE = 'substance_abuse',
  DOMESTIC_VIOLENCE = 'domestic_violence',
  USER_REQUESTS_THERAPIST = 'user_requests_therapist'
}

export enum AIResponseType {
  PRACTICE_SUGGESTIONS = 'practice_suggestions',
  ESCALATION_TO_THERAPIST = 'escalation_therapist',
  ESCALATION_TO_CRISIS = 'escalation_crisis',
  GENERAL_SUPPORT = 'general_support',
  DISCLAIMER = 'disclaimer'
}

export const ESCALATION_LABELS: Record<EscalationTrigger, string> = {
  [EscalationTrigger.CRISIS]: 'Crisis general',
  [EscalationTrigger.SUICIDAL_IDEATION]: 'Ideas suicidas',
  [EscalationTrigger.SELF_HARM]: 'Autolesiones',
  [EscalationTrigger.SEVERE_ANXIETY]: 'Ansiedad severa',
  [EscalationTrigger.PANIC_ATTACK]: 'Ataque de pánico',
  [EscalationTrigger.DISSOCIATION]: 'Disociación',
  [EscalationTrigger.PSYCHOTIC_SYMPTOMS]: 'Síntomas psicóticos',
  [EscalationTrigger.SUBSTANCE_ABUSE]: 'Abuso de sustancias',
  [EscalationTrigger.DOMESTIC_VIOLENCE]: 'Violencia doméstica',
  [EscalationTrigger.USER_REQUESTS_THERAPIST]: 'Usuario pide hablar con terapeuta'
};

// ============================================
// PATIENT AI ASSISTANT CONFIG
// ============================================

export interface PatientAIAssistantConfig {
  id: string;
  therapistId: string;
  patientId: string;
  relationshipId: string;
  
  // Configuración general
  enabled: boolean;
  
  // Nombre personalizado del asistente (opcional)
  assistantName?: string;       // Default: "Asistente APPMOR"
  
  // Prácticas permitidas (IDs de userPracticeLibrary que puede sugerir)
  allowedPracticeIds: string[];
  
  // Situaciones que el asistente puede manejar
  handleableSituations: {
    [key in AISituationType]?: boolean;
  };
  
  // Situaciones que SIEMPRE escalan a profesional
  // Estas NO pueden desactivarse por seguridad
  alwaysEscalate: {
    [key in EscalationTrigger]: boolean;
  };
  
  // Configuración de respuestas
  responseConfig: {
    maxPracticesPerSuggestion: number;  // Default: 3
    includeBreathingExercise: boolean;  // Siempre incluir respiración en ansiedad
    showMoodTracking: boolean;          // Preguntar por estado de ánimo
    offerContactTherapist: boolean;     // Siempre mostrar opción de contactar
  };
  
  // Mensajes personalizados del terapeuta
  customMessages: {
    welcomeMessage?: string;
    afterSuggestionMessage?: string;
    escalationMessage?: string;
    disclaimerOverride?: string;
  };
  
  // Información de contacto para crisis
  crisisInfo: {
    therapistPhone?: string;
    therapistEmail?: string;
    preferredContactMethod?: 'phone' | 'email' | 'app_message';
    crisisLineNumber: string;           // Default: "024" (España)
    emergencyNumber: string;            // Default: "112"
  };
  
  // Horario de disponibilidad del terapeuta (para saber cuándo escalar)
  therapistAvailability?: {
    monday?: { start: string; end: string };
    tuesday?: { start: string; end: string };
    wednesday?: { start: string; end: string };
    thursday?: { start: string; end: string };
    friday?: { start: string; end: string };
    saturday?: { start: string; end: string };
    sunday?: { start: string; end: string };
  };
  
  // Estadísticas
  stats: {
    totalInteractions: number;
    practicesSuggested: number;
    practicesAccepted: number;
    escalationsToTherapist: number;
    escalationsToCrisis: number;
    lastInteractionAt?: string;
  };
  
  // Auditoría
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

// ============================================
// AI ASSISTANT INTERACTION LOG
// ============================================

export interface AIAssistantInteraction {
  id: string;
  
  // Relaciones
  userId: string;
  therapistId?: string;           // Si tiene terapeuta vinculado
  configId?: string;              // ID de PatientAIAssistantConfig
  
  // Input del usuario
  userInput: {
    text: string;
    timestamp: string;
    
    // Contexto adicional (opcional)
    currentMood?: number;         // 1-10
    recentPractices?: string[];   // Prácticas recientes
  };
  
  // Análisis de la IA
  aiAnalysis: {
    detectedSituation: AISituationType | null;
    detectedSeverity: 'low' | 'medium' | 'high' | 'critical';
    detectedEscalationTriggers: EscalationTrigger[];
    keywords: string[];
    confidence: number;           // 0-1
  };
  
  // Respuesta generada
  response: {
    type: AIResponseType;
    message: string;
    
    // Si es sugerencia de prácticas
    suggestedPracticeIds?: string[];
    
    // Si es escalación
    escalationType?: 'therapist' | 'crisis_line' | 'emergency';
    escalationReason?: string;
    
    // Recursos adicionales mostrados
    showedCrisisInfo: boolean;
    showedContactTherapist: boolean;
  };
  
  // Acción del usuario después de la respuesta
  userAction?: {
    selectedPracticeId?: string;
    startedPractice: boolean;
    completedPractice: boolean;
    contactedTherapist: boolean;
    calledCrisisLine: boolean;
    dismissedAt?: string;
    feedbackRating?: number;      // 1-5 si el usuario valora la respuesta
    feedbackText?: string;
  };
  
  // Estado de revisión
  review: {
    therapistReviewed: boolean;
    reviewedAt?: string;
    reviewedBy?: string;
    therapistNotes?: string;
    flaggedForFollowUp: boolean;
  };
  
  // Metadata
  timestamp: string;
  sessionId?: string;             // Para agrupar interacciones de una sesión
  deviceInfo?: {
    platform: 'ios' | 'android' | 'web';
    appVersion: string;
  };
}

// ============================================
// DEFAULT CONFIG
// ============================================

export const DEFAULT_AI_ASSISTANT_CONFIG: Omit<PatientAIAssistantConfig, 'id' | 'therapistId' | 'patientId' | 'relationshipId' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'> = {
  enabled: false,  // Por defecto desactivado hasta que el terapeuta lo active
  
  allowedPracticeIds: [],
  
  handleableSituations: {
    [AISituationType.ANXIETY_MILD]: true,
    [AISituationType.STRESS]: true,
    [AISituationType.SLEEP_ISSUES]: true,
    [AISituationType.GENERAL_WELLBEING]: true,
    [AISituationType.MORNING_ROUTINE]: true,
    [AISituationType.EVENING_ROUTINE]: true,
    [AISituationType.BREAK_TIME]: true,
    // Más delicadas - por defecto desactivadas
    [AISituationType.ANXIETY_MODERATE]: false,
    [AISituationType.LOW_MOOD]: false,
    [AISituationType.OVERWHELM]: false,
    [AISituationType.ANGER]: false,
    [AISituationType.SADNESS]: false,
    [AISituationType.LONELINESS]: false,
    [AISituationType.FOCUS_ISSUES]: false,
    [AISituationType.BEFORE_MEETING]: true,
  },
  
  alwaysEscalate: {
    [EscalationTrigger.CRISIS]: true,
    [EscalationTrigger.SUICIDAL_IDEATION]: true,
    [EscalationTrigger.SELF_HARM]: true,
    [EscalationTrigger.SEVERE_ANXIETY]: true,
    [EscalationTrigger.PANIC_ATTACK]: true,
    [EscalationTrigger.DISSOCIATION]: true,
    [EscalationTrigger.PSYCHOTIC_SYMPTOMS]: true,
    [EscalationTrigger.SUBSTANCE_ABUSE]: true,
    [EscalationTrigger.DOMESTIC_VIOLENCE]: true,
    [EscalationTrigger.USER_REQUESTS_THERAPIST]: true,
  },
  
  responseConfig: {
    maxPracticesPerSuggestion: 3,
    includeBreathingExercise: true,
    showMoodTracking: true,
    offerContactTherapist: true,
  },
  
  customMessages: {},
  
  crisisInfo: {
    crisisLineNumber: '024',
    emergencyNumber: '112',
  },
  
  stats: {
    totalInteractions: 0,
    practicesSuggested: 0,
    practicesAccepted: 0,
    escalationsToTherapist: 0,
    escalationsToCrisis: 0,
  },
};

// ============================================
// DTOs
// ============================================

export interface CreateAIAssistantConfigDTO {
  therapistId: string;
  patientId: string;
  relationshipId: string;
  enabled?: boolean;
  allowedPracticeIds?: string[];
  handleableSituations?: PatientAIAssistantConfig['handleableSituations'];
  customMessages?: PatientAIAssistantConfig['customMessages'];
  crisisInfo?: Partial<PatientAIAssistantConfig['crisisInfo']>;
}

export interface UpdateAIAssistantConfigDTO {
  enabled?: boolean;
  assistantName?: string;
  allowedPracticeIds?: string[];
  handleableSituations?: PatientAIAssistantConfig['handleableSituations'];
  responseConfig?: Partial<PatientAIAssistantConfig['responseConfig']>;
  customMessages?: Partial<PatientAIAssistantConfig['customMessages']>;
  crisisInfo?: Partial<PatientAIAssistantConfig['crisisInfo']>;
  therapistAvailability?: PatientAIAssistantConfig['therapistAvailability'];
}

export interface AIAssistantQueryDTO {
  userId: string;
  text: string;
  currentMood?: number;
  sessionId?: string;
}

// ============================================
// CRISIS RESOURCES (Immutable)
// ============================================

export const CRISIS_RESOURCES = {
  spain: {
    crisisLine: '024',
    emergency: '112',
    description: 'Línea de atención a la conducta suicida (España)',
  },
  mexico: {
    crisisLine: '800 290 0024',
    emergency: '911',
    description: 'Línea de la Vida (México)',
  },
  argentina: {
    crisisLine: '135',
    emergency: '107',
    description: 'Centro de Asistencia al Suicida (Argentina)',
  },
  colombia: {
    crisisLine: '106',
    emergency: '123',
    description: 'Línea 106 Salud Mental (Colombia)',
  },
  chile: {
    crisisLine: '600 360 7777',
    emergency: '131',
    description: 'Salud Responde (Chile)',
  },
};

// ============================================
// SAFETY KEYWORDS (For detection)
// ============================================

export const SAFETY_KEYWORDS = {
  suicidal: [
    'suicid', 'quitar la vida', 'matarme', 'no quiero vivir', 'acabar con todo',
    'desaparecer', 'mejor muerto', 'no vale la pena', 'terminar con mi vida'
  ],
  selfHarm: [
    'cortarme', 'hacerme daño', 'autolesion', 'lastimarme', 'herirme'
  ],
  crisis: [
    'crisis', 'emergencia', 'no puedo más', 'ayuda urgente', 'desesperado'
  ],
  panicAttack: [
    'ataque de pánico', 'no puedo respirar', 'me voy a morir', 'corazón muy rápido'
  ],
};





